import { each, find } from 'lodash-es';

import { EMPTY_ARR } from '~/core/shared/constants';
import { StylesConfig, TenantIconsMap } from '~/core/shared/types';
import { RouterTab } from '../../components/application';
import {
  DocumentManagementDefinition,
  DocumentManagementType,
  FormConfig,
  Formlinks,
  TenantDocument,
  TenantDocumentDefinition,
  TenantDocumentDetailsView,
  TenantDocumentDetailsViewTab,
  TenantDocumentRouteProps,
  TenantDocumentView,
  TenantNavigationItems,
  TenantSettingsNavigationItem,
} from '../../config';
import { MIDDLE_OFFICE_ROUTES } from '../../routing/routes';
import { MiddleOfficeConfig } from './types';

export class MiddleOfficeConfigClassDEPRECATED {
  config: MiddleOfficeConfig<TenantDocumentDefinition>;

  constructor(config: MiddleOfficeConfig<TenantDocumentDefinition>) {
    config.documents.forEach((document) => {
      const clonedDocument = { ...document };
      each(clonedDocument.views, (view, key) => {
        clonedDocument.views[key] = this.bootView(view);
      });
    });

    this.config = config;
  }

  private bootView(view: TenantDocumentView): TenantDocumentView {
    const clonedView = { ...view };
    if (clonedView.type === 'documentDetails') {
      clonedView.tabs = this.ensureRouterTabIsAvailable(clonedView.tabs);
    }
    return clonedView;
  }

  getNavigationItems(): TenantNavigationItems[] {
    return this.config.navigation;
  }

  getSettingsNavigationItems(): TenantSettingsNavigationItem[] {
    return this.config.settingsNavigation ?? EMPTY_ARR;
  }

  getDataListDefinitions(): DocumentManagementDefinition[] {
    return (
      this.config.documentManagements
        ?.filter(
          (management) => management.for === DocumentManagementType.DataList,
        )
        .flatMap((management) => management.definitions) ?? []
    );
  }

  getIcons(path: string): TenantIconsMap | undefined {
    const icons: (TenantIconsMap | undefined)[] = [];
    this.config.documents.forEach((document) => {
      if (document.definition === path) {
        icons.push(document.icons);
      }
    });
    return icons[0];
  }

  getFormConfig(): FormConfig | undefined {
    return this.config.formConfig;
  }

  getFormConfigForDocumentType(type: string): FormConfig | undefined {
    const foundDocument = this.config.documents.find(
      (document) => document.definition === type,
    );
    return foundDocument ? foundDocument.formConfig : undefined;
  }

  getFormLinks(documentType?: string): Formlinks[] | undefined {
    if (documentType) {
      const doc = this.config.documents.find(
        (d) => d.definition === documentType || d.key === documentType,
      );
      return doc?.formLinks || undefined;
    }
    let links: Formlinks[] = [];
    this.config.documents.forEach((document) => {
      if (document.formLinks) {
        links = links.concat(document.formLinks);
      }
    });
    return links.length >= 1 ? links : undefined;
  }

  getStyles(key: string): StylesConfig {
    return this.config.styles?.[key] || ({} as StylesConfig);
  }

  getLabel(namespace: string, key: string, defaultValue: string): string {
    return this.config.labels?.[namespace]?.[key] ?? defaultValue;
  }

  private getDocumentConfig(
    key: string,
  ): TenantDocument<TenantDocumentDefinition> {
    const conf = find(this.config.documents, (doc) => doc.key === key);
    if (!conf) {
      throw new Error(
        `Could not find tenant document configuration for key "${key}"`,
      );
    }
    return conf;
  }

  private ensureRouterTabIsAvailable(
    tabs: TenantDocumentDetailsViewTab[],
  ): TenantDocumentDetailsViewTab[] {
    return [
      {
        component: RouterTab,
        hidden: true,
        key: 'router',
        route: {
          path: MIDDLE_OFFICE_ROUTES.application.path,
        },
        text: 'Router',
        type: 'component',
      },
      ...tabs,
    ];
  }

  getViewConfig({
    tenantDocumentDefinition,
    tenantDocumentKey,
    tenantViewName,
    tenantNavigationItem,
    tenantTabName,
  }: TenantDocumentRouteProps): TenantDocumentView {
    const view = {
      ...this.getDocumentConfig(tenantDocumentKey).views[tenantViewName],
      documentRouteProps: {
        tenantDocumentDefinition,
        tenantDocumentKey,
        tenantNavigationItem,
        tenantTabName,
        tenantViewName,
      },
    };
    return view;
  }

  getDetailsViewTabConfig(
    routeProps: TenantDocumentRouteProps,
  ): TenantDocumentDetailsViewTab | undefined {
    const viewConfig = this.getViewConfig(
      routeProps,
    ) as TenantDocumentDetailsView;
    return find(viewConfig.tabs, (tab) => tab.key === routeProps.tenantTabName);
  }
}
